/**
 * Atlas Intelligence > Market > Find (Console V4): the server-paged catalog table and its
 * tiles (src/aithora/v3/atlas/find.ts, find-v4.ts), plus the row actions it uses.
 * Score, distress, value and upside are MODELED; occupancy and stars are CMS-reported.
 */
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';

const A = AITHORA_PREFIX;

export type FindLens = 'ranked' | 'distress' | 'sourcing' | 'coverage';
export type FindSignal = 'Agency' | 'Distress' | 'Quality gap' | 'Turnaround' | 'Steady';

export interface FindQuery {
  lens?: FindLens;
  state?: string;
  type?: string;
  bedBand?: string;
  q?: string;
  limit?: number;
  cursor?: string;
  [k: string]: string | number | undefined;
}

/** One Find row (the V4 fields; the V3 Find fields come along and are typed loosely). */
export interface FindRow {
  id: string;
  ccn: string | null;
  name: string;
  city: string | null;
  state: string | null;
  type: string | null;
  typeLabel: string;
  unitType: string | null;
  beds: number | null;
  stars: number | null;
  occupancy: number | null;
  /** Occupancy snapshots over the last 12 months, oldest first; null when none. */
  occupancyTrend: { asof: string; v: number }[] | null;
  signal: FindSignal;
  /** $M, modeled. */
  money: { value: number | null; upside: number | null; basis: 'modeled' };
  /** Score percentile among the same type in the same state (0-100), or null. */
  peerPct: number | null;
  score: { v: number | null; grade: string | null; basis: 'modeled' };
  watched: boolean;
  held: boolean;
  /**
   * Revenue by payer ($M a year, largest first) from the one payer-mix source (AF-1658):
   * label 'COST REPORT FY2024' from the building's CMS cost report, else 'MODELED' (flat
   * split) or 'ESTIMATED' (assisted living, no Medicare). u is null when revenue is unknown.
   */
  payers?: {
    label: string; basis: 'cost_report' | 'modeled' | 'estimated'; fiscalYear: number | null;
    occupancyKnown: boolean; revenueBasis: 'cost_report' | 'modeled' | 'unknown';
    rows: { n: string; u: number | null; share: number }[]; note: string;
  };
  [k: string]: unknown;
}

export interface FindPage {
  real: boolean;
  modeled: boolean;
  rows: FindRow[];
  nextCursor: string | null;
  footer?: { source: string; catalogSize: number | null; scoringVersion: string | null; asOf: string | null } | null;
}

export interface FindSummary {
  real: boolean;
  residential: number;
  agencies: number;
  distress: number;
  sourcing: number;
  upsideM: number;
  upsideRows: number;
  /** Rows the request's filter (lens, state, type, beds, search) matches. */
  matching: number;
}

/** GET /intelligence/find: one page of the catalog through a lens and filters. */
export const intelFind = route<FindPage, FindQuery>()('GET', `${A}/intelligence/find`, { honest: true, readOnlySafe: true });
/** GET /intelligence/find/summary: the tiles (catalog-wide) and the filter's match count. */
export const intelFindSummary = route<FindSummary, FindQuery>()('GET', `${A}/intelligence/find/summary`, { honest: true, readOnlySafe: true });
/** DELETE /intelligence/watch/:facilityId: stop watching (sealed to the Truth Ledger). */
export const intelUnwatch = route<{ real: boolean; removed: boolean }>()('DELETE', `${A}/intelligence/watch/:facilityId`, { honest: true });
/** POST /intelligence/crm: open a Sales CRM deal for each facility (Sales tracks here). */
export const intelSendToCrm = route<{ real: boolean; [k: string]: unknown }, void, { facilityIds: string[] }>()('POST', `${A}/intelligence/crm`, { honest: true });
